import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { BadgeCheck, Loader2 } from 'lucide-react'
import {
  verifyCertificatePublic,
  CERTIFICATE_VERIFY_GENERIC_FAILURE
} from '@lib/certificateVerification'

const inputClass =
  'block w-full px-4 py-3 rounded-md border border-neutral-600 bg-navy-900/80 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500 text-sm'

export default function CertificateVerifySection() {
  const [certificateId, setCertificateId] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [dateIso, setDateIso] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setResult(null)
    try {
      const r = await verifyCertificatePublic({
        certificateId,
        name: recipientName,
        dateIso
      })
      setResult(r)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section-padding bg-black relative overflow-hidden border-y border-neutral-800/80">
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/90 via-black to-navy-950/80" />
      <div className="w-full px-6 sm:px-8 lg:px-12 relative max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass-effect-dark rounded-2xl border border-primary-500/20 p-6 sm:p-8"
        >
          <div className="flex items-start gap-3 mb-6">
            <div className="rounded-xl bg-primary-500/15 p-2.5 border border-primary-500/30 shrink-0">
              <BadgeCheck className="w-6 h-6 text-primary-400" aria-hidden />
            </div>
            <div>
              <h2 className="text-heading-1 text-white mb-2">Verify a certificate</h2>
              <p className="text-sm text-neutral-400 leading-relaxed">
                Enter the certificate ID, recipient name, and completion date exactly as shown on the Total Rewards Pro
                practice assessment certificate. This checks our public issuance record only — it does not reveal scores
                or contact details.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="verify-cert-id" className="block text-sm font-medium text-neutral-300 mb-1.5">
                Certificate ID
              </label>
              <input
                id="verify-cert-id"
                type="text"
                autoComplete="off"
                spellCheck="false"
                placeholder="e.g. TRP-GLOBAL-…"
                value={certificateId}
                onChange={(e) => setCertificateId(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="verify-cert-name" className="block text-sm font-medium text-neutral-300 mb-1.5">
                Name on certificate
              </label>
              <input
                id="verify-cert-name"
                type="text"
                autoComplete="name"
                placeholder="As printed on the certificate"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="verify-cert-date" className="block text-sm font-medium text-neutral-300 mb-1.5">
                Completion date
              </label>
              <input
                id="verify-cert-date"
                type="date"
                value={dateIso}
                onChange={(e) => setDateIso(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                Use the calendar date of completion (same day as on the certificate PDF).
              </p>
            </div>
            <button type="submit" className="btn-accent w-full sm:w-auto justify-center" disabled={busy}>
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  Checking…
                </span>
              ) : (
                'Verify certificate'
              )}
            </button>
          </form>

          {result?.ok === true ? (
            <div
              className="mt-6 rounded-xl border border-primary-500/35 bg-primary-500/10 px-4 py-4 text-sm text-neutral-200 space-y-2"
              role="status"
            >
              <p className="text-primary-300 font-semibold">Certificate verified</p>
              <p>
                <span className="text-neutral-500">Recipient:</span>{' '}
                <span className="text-white font-medium">{result.record.displayName}</span>
              </p>
              <p>
                <span className="text-neutral-500">Completed:</span>{' '}
                <span className="text-white font-medium">{result.record.completedDateLabel}</span>
              </p>
              {result.record.quizTitle ? (
                <p>
                  <span className="text-neutral-500">Program:</span>{' '}
                  <span className="text-white font-medium">{result.record.quizTitle}</span>
                  {result.record.trackLabel ? (
                    <span className="text-neutral-500"> ({result.record.trackLabel})</span>
                  ) : null}
                </p>
              ) : null}
              <p className="text-xs text-neutral-500 pt-2 border-t border-white/10">
                Practice assessment issued by Total Rewards Pro LLP for professional development. Not an accredited
                third-party industry certification.
              </p>
            </div>
          ) : null}

          {result?.ok === false ? (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-950/25 px-4 py-3 text-sm text-red-100" role="alert">
              {result.message || CERTIFICATE_VERIFY_GENERIC_FAILURE}
            </div>
          ) : null}

          <p className="mt-6 text-xs text-neutral-600 leading-relaxed">
            Certificates are issued only for passed attempts on our{' '}
            <Link to="/certification" className="text-primary-400 hover:text-primary-300 underline-offset-2 hover:underline">
              certification practice assessments
            </Link>
            . Records created before public verification was enabled may not verify here.
          </p>
        </motion.div>
      </div>
    </section>
  )
}
